import type { DemoStep, DrillStep, ExplainStep, Lesson } from '../schema.js';

function explain(body: string, narration: string): ExplainStep {
  return { kind: 'explain', title: 'How it moves', body, narration };
}

function demo(fen: string, from: string, to: string, body: string): DemoStep {
  return { kind: 'demo', title: 'Watch it move', body, fen, highlight: { from, to } };
}

function drill(fen: string, body: string, hint: string): DrillStep {
  return {
    kind: 'drill',
    task: 'legal-move',
    title: 'Your turn',
    body,
    fen,
    hint,
  };
}

function movesLesson(opts: {
  id: string;
  title: string;
  how: string;
  narration: string;
  demoFen: string;
  demoFrom: string;
  demoTo: string;
  drillFen: string;
  hint: string;
}): Lesson {
  return {
    id: `moves-${opts.id}`,
    slug: `moves-${opts.id}`,
    title: opts.title,
    audienceLevel: 'beginner',
    concepts: ['moves'],
    steps: [
      explain(`How does the ${opts.id} move? ${opts.how}`, opts.narration),
      demo(opts.demoFen, opts.demoFrom, opts.demoTo, 'Watch how it moves on the board.'),
      drill(opts.drillFen, `Tap a ${opts.id}, then tap any square it can reach.`, opts.hint),
    ],
  };
}

export const MOVE_LESSONS: Lesson[] = [
  movesLesson({
    id: 'knight',
    title: 'The knight hops in an L',
    how: 'A knight moves two squares one way and then one square sideways. It can jump over other pieces!',
    narration: 'The knight hops in an L shape and can jump over friends.',
    demoFen: '4k3/8/8/8/3N4/8/8/4K3 w - - 0 1',
    demoFrom: 'd4',
    demoTo: 'f5',
    drillFen: '4k3/8/8/8/8/2N5/8/4K3 w - - 0 1',
    hint: 'A knight on c3 can reach a2, b1, d1, e2, e4, d5, b5, or a4.',
  }),
  movesLesson({
    id: 'bishop',
    title: 'The bishop slides diagonally',
    how: 'A bishop moves any number of squares diagonally. It always stays on squares of the same color.',
    narration: 'The bishop slides along long diagonals.',
    demoFen: '4k3/8/8/8/3B4/8/8/4K3 w - - 0 1',
    demoFrom: 'd4',
    demoTo: 'g7',
    drillFen: '4k3/8/8/8/8/8/2B5/4K3 w - - 0 1',
    hint: 'A bishop on c2 can slide to a4, b3, d3, e4, f5, g6, h7, b1, or d1.',
  }),
  movesLesson({
    id: 'rook',
    title: 'The rook slides straight',
    how: 'A rook moves any number of squares up, down, left, or right — but never diagonally.',
    narration: 'The rook slides in straight lines.',
    demoFen: '4k3/8/8/8/3R4/8/8/4K3 w - - 0 1',
    demoFrom: 'd4',
    demoTo: 'd8',
    drillFen: '4k3/8/8/8/8/8/8/R3K3 w - - 0 1',
    hint: 'A rook on a1 can slide along the a-file or the first rank.',
  }),
  movesLesson({
    id: 'queen',
    title: 'The queen slides everywhere',
    how: 'A queen moves like a rook and a bishop together — straight or diagonal, any distance.',
    narration: 'The queen slides in every direction.',
    demoFen: '4k3/8/8/8/3Q4/8/8/4K3 w - - 0 1',
    demoFrom: 'd4',
    demoTo: 'h8',
    drillFen: '4k3/8/8/8/3Q4/8/8/4K3 w - - 0 1',
    hint: 'A queen on d4 can slide along the d-file, the fourth rank, or either diagonal.',
  }),
  {
    id: 'sliding-stars',
    slug: 'moves-sliding-stars',
    title: 'Sliding stars — make it count',
    audienceLevel: 'beginner',
    concepts: ['moves'],
    steps: [
      {
        kind: 'explain',
        title: 'Put your sliding pieces to work',
        body: 'Your sliding pieces are strongest when they are active. Find the best move for the bishop.',
        narration: 'Find the best move with a sliding piece.',
      },
      {
        kind: 'drill',
        task: 'best-move',
        title: 'Win the rook',
        body: 'Your bishop can win a whole rook. Find the best move!',
        fen: '4k3/8/8/8/2r5/8/4B3/4K3 w - - 0 1',
        answer: ['Bxc4'],
        hint: 'The bishop on e2 can capture the rook on c4.',
      },
      {
        kind: 'quiz',
        title: 'Sliding quiz',
        body: 'A quick check on your sliding pieces.',
        questions: [
          { prompt: 'Which piece slides only on diagonals?', choices: ['Rook', 'Bishop', 'Pawn'], correct: 1 },
          { prompt: 'Which piece can move the farthest in one turn?', choices: ['Pawn', 'Knight', 'Queen'], correct: 2 },
        ],
      },
    ],
  },
  {
    id: 'move-champion',
    slug: 'moves-champion',
    title: 'Move like a champion',
    audienceLevel: 'beginner',
    concepts: ['moves'],
    steps: [
      {
        kind: 'explain',
        title: 'Any move, any piece',
        body: 'In this drill you may move ANY of your pieces. Every move must be legal — that is how a champion practices.',
        narration: 'Make any legal move you like.',
      },
      {
        kind: 'drill',
        task: 'legal-move',
        free: true,
        title: 'Free play',
        body: 'Tap a piece, then tap a square. Any legal move counts!',
        fen: '4k3/8/8/8/3N1B2/8/PPP4P/4K3 w - - 0 1',
      },
    ],
  },
];
